import { useCallback } from 'react';
import { AppstoreOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Blocks } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '../../../components/shared';
import { Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ghep } from '../../../lib/duong-dan-cong';
import { VungRemote } from '../components/VungRemote';
import { useModuleDaCai } from '../hooks/useModuleDaCai';
import { GOC_MAN_MODULE, menuDangXem, tenNhomMenu } from '../nghiep-vu/menu';

/**
 * Màn của một module đã cài, nạp từ remote Module Federation (ADR-0096). Route `/client/m/:code/*`;
 * phần sau mã module truyền nguyên cho remote làm `path`. Cổng vẽ `PageHeader` như mọi màn khác
 * (breadcrumb "tên module › mục đang xem"), remote chỉ vẽ phần thân.
 */
export function ManHinhModuleView() {
  const { code = '', '*': duoi = '' } = useParams();
  const { t } = useT();
  const dichLoi = useLoi();
  const navigate = useNavigate();
  const dsModule = useModuleDaCai();
  const module = dsModule.data?.find((m) => m.code === code);

  const basePath = ghep(`${GOC_MAN_MODULE}/${code}`, 'tenant');
  const path = `/${duoi}`;
  const dieuHuong = useCallback(
    (p: string) => navigate(`${basePath}${p === '/' ? '' : p.startsWith('/') ? p : `/${p}`}`),
    [navigate, basePath],
  );

  const than = (() => {
    if (dsModule.isError) {
      return (
        <Empty
          icon={WarningOutlined}
          title={t('moduleDaCai.loiDanhSach.tieuDe')}
          description={dichLoi(dsModule.error)}
          action={
            <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={() => void dsModule.refetch()}>
              {t('moduleDaCai.thuLai')}
            </Button>
          }
        />
      );
    }
    if (dsModule.isPending) {
      return <Skeleton className="h-64 w-full" />;
    }
    if (!module) {
      return (
        <Empty
          icon={AppstoreOutlined}
          title={t('moduleDaCai.chuaCai.tieuDe')}
          description={t('moduleDaCai.chuaCai.mo', { code })}
        />
      );
    }
    if (!module.frontend_remote) {
      return (
        <Empty
          icon={AppstoreOutlined}
          title={t('moduleDaCai.khongGiaoDien.tieuDe', { ten: module.name })}
          description={t('moduleDaCai.khongGiaoDien.mo')}
        />
      );
    }
    return (
      <VungRemote
        code={module.code}
        version={module.version}
        entry={module.frontend_remote}
        ten={module.name}
        basePath={basePath}
        path={path}
        navigate={dieuHuong}
      />
    );
  })();

  const tenModule = module?.name ?? code;
  const mucHienTai = module ? menuDangXem(module, path) : undefined;
  const nhomHienTai = tenNhomMenu(mucHienTai?.group);

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-12">
        <PageHeader
          breadcrumb={[
            {
              label: tenModule,
              onClick: mucHienTai && mucHienTai.path !== '/' ? () => dieuHuong('/') : undefined,
            },
            // Nhóm cha (ADR-0121) không có trang riêng nên không bấm được.
            ...(nhomHienTai ? [{ label: nhomHienTai }] : []),
            ...(mucHienTai ? [{ label: mucHienTai.label }] : []),
          ]}
          titleIcon={<Blocks size={18} className="text-primary-600" />}
          title={mucHienTai?.label ?? tenModule}
          subtitle={module ? `${module.code} · ${module.version}` : undefined}
        />
        {than}
      </div>
    </div>
  );
}
